<template>
  <div class="chart-container" :style="{ height: height }">
    <div v-if="loading" class="chart-loading">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <div v-else-if="error" class="chart-error">
      <i class="fas fa-exclamation-circle"></i>
    </div>
    <v-chart 
      v-else 
      class="chart" 
      :option="chartOption" 
      autoresize 
      @click="handleChartClick"
      ref="chart"
    />
    
    <!-- 图片预览组件 -->
    <div v-if="showPreview" class="image-preview-overlay" @click="closePreview">
      <div class="image-preview-container" @click.stop>
        <div class="preview-images">
          <div class="preview-image-wrapper">
            <h3>{{ currentImageType === 'raw' ? '原始图片' : '预测图片' }}</h3>
            <img 
              :src="currentImageType === 'raw' ? previewImages.raw : previewImages.dist" 
              :style="{ transform: `scale(${previewScale})` }" 
            />
          </div>
        </div>
        <div class="preview-controls">
          <button @click="switchImage" title="切换图片 (Tab)">
            {{ currentImageType === 'raw' ? '查看预测图片' : '查看原始图片' }}
          </button>
          <button @click="zoomIn" title="放大 (Ctrl + +)">+</button>
          <button @click="zoomOut" title="缩小 (Ctrl + -)">-</button>
          <button @click="closePreview" title="关闭 (ESC)">✕</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
]);

export default {
  name: 'AudienceChart',
  components: {
    VChart,
  },
  props: {
    artist: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    stage: {
      type: String,
      required: true,
    },
    height: {
      type: String,
      default: '200px',
    },
    data: {
      typeof: Object,
      default: {}
    },
    imageData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      chartData: [],
      showPreview: false,
      previewImages: {
        raw: '',
        dist: ''
      },
      previewScale: 1,
      imageBaseUrl: 'https://crowd-counting.chunjuan.fun/MFfile',
      currentImageType: 'raw'
    };
  },
  created() {
    this.processData(this.data)
  },
  mounted() {
    if (this.$refs.chart) {
      this.$refs.chart.chart.on('click', this.handleChartClick);
    }
    // 添加键盘事件监听
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    if (this.$refs.chart) {
      this.$refs.chart.chart.off('click', this.handleChartClick);
    }
    // 移除键盘事件监听
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    processData(rawData) {
      const data = JSON.parse(JSON.stringify(rawData))
      this.chartData = data
        .filter(item => item && typeof item === 'object')
        .map(item => ({
          time: item.rounded_datetime || '',
          count: Number(item.area_count) || 0
        }))
        .filter(item => item.time && !isNaN(item.count))
    },
    
    handleChartClick(params) {
      console.log('Chart clicked:', params);
      
      if (params.componentType === 'series') {
        const seriesIndex = params.seriesIndex;
        const dataIndex = params.dataIndex;
        
        const currentSeries = this.data[seriesIndex];
        if (!currentSeries || !currentSeries.data[dataIndex]) {
          console.log('No series data found');
          return;
        }
        
        // 直接使用 dataIndex 获取数据点
        const dataPoint = currentSeries.data[dataIndex];
        
        console.log('Found data point:', dataPoint);
        
        if (dataPoint && dataPoint.new_raw_image_path && dataPoint.new_dist_image_path) {
          this.previewImages = {
            raw: `${this.imageBaseUrl}/${dataPoint.new_raw_image_path}`,
            dist: `${this.imageBaseUrl}/${dataPoint.new_dist_image_path}`
          };
          console.log('Setting preview images:', this.previewImages);
          this.showPreview = true;
          this.previewScale = 1;
        } else {
          console.log('No image data found for this point');
        }
      }
    },
    
    closePreview() {
      this.showPreview = false;
      this.previewImages = {
        raw: '',
        dist: ''
      };
      this.previewScale = 1;
      this.currentImageType = 'raw';
    },
    
    zoomIn() {
      this.previewScale = Math.min(this.previewScale + 0.2, 3);
    },
    
    zoomOut() {
      this.previewScale = Math.max(this.previewScale - 0.2, 0.5);
    },
    
    handleKeydown(event) {
      if (this.showPreview) {
        if (event.key === 'Escape') {
          this.closePreview();
        } else if (event.key === '+' || event.key === '=') {
          this.zoomIn();
        } else if (event.key === '-' || event.key === '_') {
          this.zoomOut();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.switchImage();
        }
      }
    },
    
    switchImage() {
      this.currentImageType = this.currentImageType === 'raw' ? 'dist' : 'raw';
    }
  },
  computed: {
    chartOption() {
      const rawSeries = this.data || [];

      if (!rawSeries.length || !rawSeries[0].data?.length) return {
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: []
      };

      const xAxisLabels = rawSeries[0].data.map(item => {
        const parts = item.rounded_datetime.split(' ');
        return parts[1] || item.rounded_datetime;
      });

      const series = rawSeries.map(s => ({
        name: s.area,
        type: 'line',
        stack: 'total',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 2
          }
        },
        areaStyle: {},
        lineStyle: {
          width: 2,
        },
        data: s.data.map(d => Number(d.area_count) || 0),
      }));

      return {
        animation: false,
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const time = params[0].name;
            let tooltipContent = `${time}<br/>`;
            
            // 检查每个系列是否有图片
            const seriesWithImages = [];
            params.forEach(param => {
              const seriesIndex = param.seriesIndex;
              const dataIndex = param.dataIndex;
              const currentSeries = this.data[seriesIndex];
              const dataPoint = currentSeries?.data[dataIndex];
              const hasImages = dataPoint?.new_raw_image_path && dataPoint?.new_dist_image_path;
              
              tooltipContent += `${param.seriesName}: ${param.value}`;
              if (hasImages) {
                tooltipContent += ' 📷';
                seriesWithImages.push(param.seriesName);
              }
              tooltipContent += '<br/>';
            });
            
            if (seriesWithImages.length > 0) {
              tooltipContent += `<br/><span style="color: #ff6b6b; font-weight: bold;">点击 ${seriesWithImages.join('、')} 区域查看图片</span>`;
            }
            
            return tooltipContent;
          }
        },
        legend: {
          data: rawSeries.map(s => s.area),
        },
        grid: {
          top: 30,
          left: 10,
          right: 10,
          bottom: 30,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisLabels,
          axisPointer: {
            type: 'line',
            label: {
              show: true
            }
          }
        },
        yAxis: {
          type: 'value',
        },
        series,
      };
    }
  },
  watch: {
    artist: 'fetchData',
    date: 'fetchData',
    stage: 'fetchData',
    data: {
      handler(newVal) {
        this.processData(newVal)
      },
      deep: true,
      immediate: false
    }
  }
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.chart {
  width: 100%;
  height: 100%;
}

.chart-loading,
.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(0, 0, 0, 0.2);
}

.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  padding: 20px;
}

.preview-images {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  min-height: fit-content;
}

.preview-image-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.preview-image-wrapper h3 {
  color: white;
  margin-bottom: 10px;
  text-align: center;
}

.preview-image-wrapper img {
  max-height: 85vh;
  object-fit: contain;
  transition: transform 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.preview-controls {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 1001;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  flex-wrap: wrap;
  max-width: 300px;
}

.preview-controls button {
  padding: 8px 16px;
  background: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  min-width: 40px;
  transition: all 0.2s ease;
  font-size: 12px;
}

.preview-controls button:first-child {
  background: #4CAF50;
  color: white;
  flex: 1;
  min-width: 120px;
}

.preview-controls button:first-child:hover {
  background: #45a049;
}

.preview-controls button:hover {
  background: #f0f0f0;
  transform: scale(1.05);
}

.preview-controls button:active {
  transform: scale(0.95);
}

.preview-controls button:last-child {
  background: #ff6b6b;
  color: white;
}

.preview-controls button:last-child:hover {
  background: #ff5252;
}
</style> 